/**
 * Converts a hex string like `#fff` or `#112233` to the `rgb()` format.
 *
 * Examples:
 * ```ts
 *  hexToRGB('#fff') == 'rgb(255,255,255)'
 *  hexToRGB('#000000') == 'rgb(0,0,0)'
 * ```
 *
 * @param hex Hex string to convert to the `rgb()` format.
 */
export function hexToRGB(hex: string): string {
    const hexValue = hex.replace('#', '');

    if (hexValue.length === 3) {
        // Hex looks like "123"
        const r = parseInt(`${hexValue[0]}${hexValue[0]}`, 16);
        const g = parseInt(`${hexValue[1]}${hexValue[1]}`, 16);
        const b = parseInt(`${hexValue[2]}${hexValue[2]}`, 16);
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
            throw new Error(
                `Expected to receive valid hex values (characters in range 0-9, a-f), got: "${hexValue}"`
            );
        }
        return `rgb(${r},${g},${b})`;
    }
    if (hexValue.length === 6) {
        // Hex looks like "112233"
        const r = parseInt(hexValue.slice(0, 2), 16);
        const g = parseInt(hexValue.slice(2, 4), 16);
        const b = parseInt(hexValue.slice(4, 6), 16);
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
            throw new Error(
                `Expected to receive valid hex values (characters in range 0-9, a-f), got: "${hexValue}"`
            );
        }
        return `rgb(${r},${g},${b})`;
    }

    // Probably not a valid hex value, throw an error
    throw new Error(
        `Expected to receive valid hex value (length 3 or 6), got: "${hexValue}"`
    );
}

/**
 * Converts a hex string like `#fff` or `#112233` to the `rgba()` format.
 *
 * Examples:
 * ```ts
 *  hexToRGBA('#fff', 0.5) == 'rgba(255,255,255,0.5)'
 *  hexToRGBA('#000000', 1) == 'rgba(0,0,0,1)'
 * ```
 *
 * @param hex Hex string to convert to the `rgba()` format.
 * @param alpha The alpha value between 0 and 1 (inclusive).
 */
export function hexToRGBA(hex: string, alpha: number): string {
    const hexValue = hex.replace('#', '');

    if (alpha < 0) {
        throw new Error(`Expected non-negative alpha value, got: ${alpha}`);
    }
    if (alpha > 1) {
        throw new Error(`Expected alpha between 0 and 1, got: ${alpha}`);
    }

    if (hexValue.length === 3) {
        // Hex looks like "123"
        const r = parseInt(`${hexValue[0]}${hexValue[0]}`, 16);
        const g = parseInt(`${hexValue[1]}${hexValue[1]}`, 16);
        const b = parseInt(`${hexValue[2]}${hexValue[2]}`, 16);
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
            throw new Error(
                `Expected to receive valid hex values (characters in range 0-9, a-f), got: "${hexValue}"`
            );
        }
        return `rgba(${r},${g},${b},${alpha})`;
    }
    if (hexValue.length === 6) {
        // Hex looks like "112233"
        const r = parseInt(hexValue.slice(0, 2), 16);
        const g = parseInt(hexValue.slice(2, 4), 16);
        const b = parseInt(hexValue.slice(4, 6), 16);
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
            throw new Error(
                `Expected to receive valid hex values (characters in range 0-9, a-f), got: "${hexValue}"`
            );
        }
        return `rgba(${r},${g},${b},${alpha})`;
    }

    // Probably not a valid hex value, throw an error
    throw new Error(
        `Expected to receive valid hex value (length 3 or 6), got: "${hexValue}"`
    );
}
